// apps/web/src/self-service/SelfServicePage.tsx — Phase 4 plan 04-04.
//
// A8 — the read-only self-service view. A guest opens the link
// from the confirmation email; the URL path is
// `/reservierung/<raw-token>` (the server builds the URL with
// `env.PUBLIC_WEB_ORIGIN + /reservierung/<encoded-token>` per
// OQ-10, so the token NEVER appears in the Host header or query
// string). The page:
//
//   1. Reads `useParams().token` and stores it in the in-memory
//      `selfServiceStore` (T-04-32: never localStorage).
//   2. Fires `Query.reservationByToken(token)` — the
//      `x-self-service-token` header is injected by the api.ts
//      header builder reading the same in-memory store.
//   3. On success, populates the store with the reservation row.
//   4. Renders the public-safe fields + two action buttons
//      ("Reservierung ändern" / "Reservierung stornieren").
//   5. If `isCancellable === false`, the action buttons are
//      disabled with the 60-minute-window caption (SELF-04).
//   6. On `SELF_SERVICE_TOKEN_INVALID` or `RESERVATION_EXPIRED`,
//      the page renders the fallback message and hides the
//      action buttons.
//
// Defenses honoured:
//   - D-04 / T-04-33: the index.html `<meta name="referrer">` is
//     `no-referrer`; the Vite dev server sets the matching header.
//     The runtime canary lives in `apps/web/test/referrer-policy.test.ts`.
//   - D-09 / T-04-14: the server returns the public-safe surface
//     (no contact fields). The page does not request them.
//   - D-26: the Turnstile widget is NOT mounted on A8 — the read
//     is low-risk (BOOK-13 / SELF-05 caps the per-token + per-IP
//     rate at the resolver layer; the page never fires a
//     mutation).

import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Clock, Users } from "lucide-react";
import { ClientError } from "graphql-request";
import {
  getReservationByToken,
  type SelfServiceReservationGql,
} from "../lib/api";
import { scrubQueryAndHash } from "./token-storage";
import {
  setSelfServiceReservation,
  setSelfServiceToken,
  useSelfServiceSlice,
} from "./state";

function asString(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function readErrorCode(err: unknown): string | null {
  if (err instanceof ClientError) {
    const code = err.response.errors?.[0]?.extensions?.code;
    return typeof code === "string" ? code : null;
  }
  if (err && typeof err === "object") {
    const e = err as { extensions?: { code?: unknown } };
    const code = e.extensions?.code;
    return typeof code === "string" ? code : null;
  }
  return null;
}

function formatLongDate(iso: string, locale: "de" | "en"): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const dt = new Date(`${iso}T00:00:00Z`);
  return new Intl.DateTimeFormat(locale === "en" ? "en-US" : "de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(dt);
}

function formatTime(iso: string, locale: "de" | "en"): string {
  if (typeof iso !== "string" || iso.length === 0) return "";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return new Intl.DateTimeFormat(locale === "en" ? "en-US" : "de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(dt);
}

export function SelfServicePage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ token: string }>();
  const rawToken = asString(params.token);
  const locale = i18n.language.startsWith("en") ? "en" : "de";

  // Defense in depth: scrub any query/hash on the URL bar (T-04-33
  // + OQ-7). MemoryRouter does NOT reflect the URL into window
  // location in tests; this call is a no-op in the MemoryRouter
  // test surface.
  useEffect(() => {
    scrubQueryAndHash();
  }, []);

  // Stash the token in the in-memory store. T-04-32: the raw
  // token NEVER leaves the module-scope object (no localStorage,
  // no sessionStorage).
  useEffect(() => {
    setSelfServiceToken(rawToken);
  }, [rawToken]);

  const query = useQuery({
    queryKey: ["self-service-reservation", rawToken],
    queryFn: () => getReservationByToken(rawToken as string),
    enabled: typeof rawToken === "string" && rawToken.length > 0,
    retry: false,
  });

  // Mirror the fetched row into the store so the A9 / A10 pages
  // don't need to refetch. The owner-only fields are NOT on the
  // SELF-01 surface (D-09); populate the store with the public-safe
  // projection + explicit nulls.
  useEffect(() => {
    if (query.data) {
      setSelfServiceReservation({
        ...query.data,
        status: normalizeStatus(query.data.status),
        guestName: "",
        guestEmail: null,
        guestPhone: null,
      });
    }
  }, [query.data]);

  const errorCode = useMemo(() => readErrorCode(query.error), [query.error]);
  const tokenInvalid =
    errorCode === "SELF_SERVICE_TOKEN_INVALID" ||
    errorCode === "RESERVATION_EXPIRED";

  if (!rawToken) {
    // No token in the URL — the page is reachable only via the
    // email link. Render the fallback.
    return (
      <main
        className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
        data-testid="a8-self-service-page"
      >
        <FallbackCard />
      </main>
    );
  }

  if (query.isLoading) {
    return (
      <main
        className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
        data-testid="a8-self-service-page"
      >
        <div
          className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm p-4"
          data-testid="a8-loading"
        >
          <p className="text-sm text-text/70">{t("wizard.a8.loading")}</p>
        </div>
      </main>
    );
  }

  if (tokenInvalid) {
    return (
      <main
        className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
        data-testid="a8-self-service-page"
      >
        <div
          className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm p-4"
          data-testid="a8-token-invalid"
          role="alert"
        >
          <p className="text-sm text-text">{t("wizard.a8.tokenInvalid")}</p>
        </div>
      </main>
    );
  }

  if (query.isError || !query.data) {
    return (
      <main
        className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
        data-testid="a8-self-service-page"
      >
        <div
          className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm p-4"
          data-testid="a8-error"
          role="alert"
        >
          <p className="text-sm text-text">{t("errors.network")}</p>
        </div>
      </main>
    );
  }

  return (
    <ResolvedView
      reservation={query.data}
      locale={locale}
      token={rawToken}
      onModify={() =>
        navigate(`/reservierung/${encodeURIComponent(rawToken)}?action=modify`)
      }
      onCancel={() =>
        navigate(`/reservierung/${encodeURIComponent(rawToken)}?action=cancel`)
      }
    />
  );
}

interface ResolvedViewProps {
  reservation: SelfServiceReservationGql;
  token: string;
  locale: "de" | "en";
  onModify: () => void;
  onCancel: () => void;
}

function ResolvedView({
  reservation,
  locale,
  onModify,
  onCancel,
}: ResolvedViewProps): JSX.Element {
  const { t } = useTranslation();
  const cancellable = reservation.isCancellable;
  const statusKey = statusToKey(reservation.status);

  const summary = useMemo(
    () => ({
      date: formatLongDate(reservation.serviceDate, locale),
      time: formatTime(reservation.startsAt, locale),
      party: t("wizard.a7.partySize", { count: reservation.partySize }),
      duration: formatDuration(reservation.durationMinutes, locale),
      confirmationMode:
        reservation.confirmationMode === "AUTO"
          ? t("wizard.a8.confirmationMode.auto")
          : t("wizard.a8.confirmationMode.manual"),
    }),
    [reservation, locale, t],
  );

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid="a8-self-service-page"
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm overflow-hidden">
        <header
          className="px-4 py-3 border-b border-divider"
          data-testid="a8-header"
        >
          <h1
            className="font-heading text-2xl md:text-3xl"
            data-testid="a8-heading"
          >
            {t("wizard.a8.title")}
          </h1>
          <p className="text-sm text-text/70 mt-0.5" data-testid="a8-subtitle">
            {t("wizard.a8.subtitle")}
          </p>
        </header>

        <div className="px-4 pt-4 space-y-3" data-testid="a8-summary">
          <Row
            label={t("wizard.a7.reservationNumberLabel")}
            value={
              <span
                className="font-mono text-lg tabular-nums"
                data-testid="a8-reservation-number"
              >
                {reservation.reservationNumber}
              </span>
            }
          />
          <Row
            label={t("wizard.a8.status.label")}
            value={
              <span
                data-testid="a8-status-tag"
                data-status={reservation.status}
                className={
                  "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium " +
                  statusClass(reservation.status)
                }
              >
                {t(statusKey)}
              </span>
            }
          />
          <Row
            label={t("wizard.a6.summary.date")}
            value={
              <span
                data-testid="a8-date"
                className="inline-flex items-center gap-2 text-sm"
              >
                <CalendarDays className="h-4 w-4" aria-hidden="true" />
                {summary.date}
              </span>
            }
          />
          <Row
            label={t("wizard.a6.summary.time")}
            value={
              <span
                data-testid="a8-time"
                className="inline-flex items-center gap-2 text-sm"
              >
                <Clock className="h-4 w-4" aria-hidden="true" />
                {summary.time}
              </span>
            }
          />
          <Row
            label={t("wizard.a6.summary.duration")}
            value={
              <span data-testid="a8-duration" className="text-sm">
                {summary.duration}
              </span>
            }
          />
          <Row
            label={t("wizard.a6.summary.party")}
            value={
              <span
                data-testid="a8-party"
                className="inline-flex items-center gap-2 text-sm"
              >
                <Users className="h-4 w-4" aria-hidden="true" />
                {summary.party}
              </span>
            }
          />
          <Row
            label={t("wizard.a8.confirmationMode.label")}
            value={
              <span data-testid="a8-confirmation-mode" className="text-sm">
                {summary.confirmationMode}
              </span>
            }
          />
        </div>

        {!cancellable && (
          <p
            className="mx-4 mt-4 text-sm text-text/80 bg-surface border border-divider rounded-md p-3"
            data-testid="a8-disabled-reason"
            role="status"
          >
            {t("wizard.a8.disabledReason")}
          </p>
        )}

        <div className="px-4 pt-4 pb-4 flex flex-col gap-2">
          <button
            type="button"
            disabled={!cancellable}
            aria-disabled={!cancellable}
            onClick={onModify}
            data-testid="a8-modify-action"
            className={`min-h-[44px] inline-flex items-center justify-center rounded-md border ${
              cancellable
                ? "border-accent text-accent-700 hover:bg-accent-100"
                : "border-divider text-text/40 cursor-not-allowed"
            }`}
          >
            {t("wizard.a8.actions.modify")}
          </button>
          <button
            type="button"
            disabled={!cancellable}
            aria-disabled={!cancellable}
            onClick={onCancel}
            data-testid="a8-cancel-action"
            className={`min-h-[44px] inline-flex items-center justify-center rounded-md border ${
              cancellable
                ? "border-red-500 text-red-700 hover:bg-red-50"
                : "border-divider text-text/40 cursor-not-allowed"
            }`}
          >
            {t("wizard.a8.actions.cancel")}
          </button>
        </div>

        <p className="px-4 pb-4 text-xs text-text/60" data-testid="a8-footer">
          {t("wizard.a8.footer")}
        </p>
      </div>
    </main>
  );
}

function Row({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-text/70 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-sm text-text text-right">{value}</span>
    </div>
  );
}

function FallbackCard(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm p-4"
      data-testid="a8-fallback"
    >
      <h1
        className="font-heading text-2xl md:text-3xl mb-2"
        data-testid="a8-fallback-title"
      >
        {t("wizard.a8.tokenInvalid")}
      </h1>
      <p className="text-sm text-text/80" data-testid="a8-fallback-body">
        {t("wizard.a8.footer")}
      </p>
      <Link
        to="/"
        className="mt-4 min-h-[44px] inline-flex items-center justify-center px-4 py-2 rounded-md border border-divider text-text"
        data-testid="a8-fallback-home"
      >
        {t("wizard.a7.home")}
      </Link>
    </div>
  );
}

function statusToKey(status: string): string {
  switch (status) {
    case "bestätigt":
      return "wizard.a8.status.confirmed";
    case "angefragt":
      return "wizard.a8.status.requested";
    case "storniert":
      return "wizard.a8.status.cancelled";
    case "no_show":
      return "wizard.a8.status.noShow";
    default:
      return "wizard.a8.status.confirmed";
  }
}

function statusClass(status: string): string {
  switch (status) {
    case "bestätigt":
      return "bg-green-100 text-green-800";
    case "angefragt":
      return "bg-amber-100 text-amber-800";
    case "storniert":
      return "bg-gray-200 text-gray-700";
    case "no_show":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-200 text-gray-700";
  }
}

function formatDuration(minutes: number, locale: "de" | "en"): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (locale === "en") {
    if (hours === 0) return `${mins} minutes`;
    if (mins === 0) return hours === 1 ? "1 hour" : `${hours} hours`;
    return `${hours} hour ${mins} minutes`;
  }
  if (hours === 0) return `${mins} Minuten`;
  if (mins === 0) return hours === 1 ? "1 Stunde" : `${hours} Stunden`;
  return `${hours} Stunde ${mins} Minuten`;
}

// `useSelfServiceSlice` is exported for parity with the wizard
// pattern (tests can mount the store in isolation) but the page
// itself reads the row from `useQuery` (the canonical source) and
// mirrors into the store via `setSelfServiceReservation`.
void useSelfServiceSlice;

// The store's `SelfServiceReservation.status` is a string-literal
// union; the GraphQL surface returns plain `string`. Narrow with
// a runtime check + a safe default.
function normalizeStatus(
  s: string,
): "bestätigt" | "angefragt" | "storniert" | "no_show" {
  if (
    s === "bestätigt" ||
    s === "angefragt" ||
    s === "storniert" ||
    s === "no_show"
  ) {
    return s;
  }
  return "bestätigt";
}
